import { useEffect, useReducer, useState, useCallback } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import {
  Circle as CircleIcon,
  RadioButtonUnchecked as OfflineIcon,
  Block as UnavailableIcon,
} from "@mui/icons-material";

import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  ptBR,
  getGridStringOperators,
  GridToolbarExportContainer,
  getDataGridUtilityClass,
  getGridDateOperators,
  getGridSingleSelectOperators,
  gridClasses,
  useGridApiRef,
  useGridApiContext,
  useGridSelector,
  gridFilteredDescendantCountLookupSelector,
  getGridNumericOperators,
} from "@mui/x-data-grid-pro";

import api from "../../../api";
import { getSession } from "../../../auth";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const STATUS_PRIORITY = {
  online: 0,
  busy: 1,
  offline: 2,
  unavailable: 3,
};

const getStatusPriority = (status) => {
  if (!status) return 99;
    return STATUS_PRIORITY[status.toLowerCase()] ?? 99;
};

const sortByStatusAndName = (rows, nameField, fallbackField) => {
  return [...rows].sort((a, b) => {
    const sA = getStatusPriority(a.status);
    const sB = getStatusPriority(b.status);
    
    if (sA !== sB) return sA - sB;    
      const nameA =
        (a[nameField] || (fallbackField ? a[fallbackField] : "") || "").toString();
      const nameB =
        (b[nameField] || (fallbackField ? b[fallbackField] : "") || "").toString();
      
      return nameA.localeCompare(nameB);
  });
};

const defaultReducer = (state, action) => {
  return { ...state, ...action };
};

// Helper function to get status display properties
const getStatusDisplay = (status) => {
  switch (status?.toLowerCase()) {
    case "online":
      return {
        icon: <CircleIcon sx={{ fontSize: 12 }} />,
        color: "#4caf50", // Green
        label: "Online",
        bgColor: "#e8f5e8",
      };
    case "busy":
      return {
        icon: <CircleIcon sx={{ fontSize: 12 }} />,
        color: "#ff9800", // Orange
        label: "Ocupado",
        bgColor: "#fff3e0",
      };
    case "offline":
      return {
        icon: <OfflineIcon sx={{ fontSize: 12 }} />,
        color: "#f44336", // Red
        label: "Offline",
        bgColor: "#ffebee",
      };
    case "unavailable":
    default:
      return {
        icon: <UnavailableIcon sx={{ fontSize: 12 }} />,
        color: "#9e9e9e", // Gray
        label: "Indisponível",
        bgColor: "#f5f5f5",
      };
  }
};

// Status render cell component for DataGrid
const StatusCell = ({ value }) => {
  const statusDisplay = getStatusDisplay(value);

  return (
    <Chip
      icon={statusDisplay.icon}
      label={statusDisplay.label}
      size="small"
      sx={{
        backgroundColor: statusDisplay.bgColor,
        color: statusDisplay.color,
        border: `1px solid ${statusDisplay.color}`,
        "& .MuiChip-icon": {
          color: statusDisplay.color,
        },
      }}
    />
  );
};

const PbxContacts = ({
  label,
  contacts,
  total,
  columns,
  page,
  pageSize,
  fetchContacts,
  loading,
  searchTerm,
  expanded,
  onToggle,
  onContactSelected,
}) => {
  return (
    <Accordion expanded={expanded} onChange={onToggle}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`queues-content-${label}`}
        id={`queues-header-${label}`}
      >
        <Typography component="span">
          {label} ({total})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <DataGridPro
          rows={contacts}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          density="compact"
          disableSelectionOnClick
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          pagination
          paginationMode="server"
          rowCount={total}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => {
            fetchContacts(newPage, pageSize, searchTerm);
          }}
          onPageSizeChange={(newPageSize) => {
            fetchContacts(0, newPageSize, searchTerm);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          loading={loading}
          onRowClick={(params, event) => {
            onContactSelected(event, params.row);
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const ContactsPanel = ({ open, onClose, handleContactSelected }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTimeoutId, setSearchTimeoutId] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState({
    queues: false,
    agents: false,
    extensions: false,
    contacts: false,
  });

  const [state, setState] = useReducer(defaultReducer, {
    crm_contacts: [],
    agent_contacts: [],
    queue_contacts: [],
    extension_contacts: [],
    crmPagination: {
      page: 0,
      pageSize: 25,
      total: 0,
      loading: false,
    },
    extensionPagination: {
      page: 0,
      pageSize: 25,
      total: 0,
      loading: false,
    },
    agentPagination: {
      page: 0,
      pageSize: 25,
      total: 0,
      loading: false,
    },
    queuePagination: {
      page: 0,
      pageSize: 25,
      total: 0,
      loading: false,
    },
  });

  const fetchCrmContacts = async (page = 0, pageSize = 25, search = "") => {
    const enterpriseId = getSession().profile.enterprise.id;
    const offset = page * pageSize;

    setState({
      crmPagination: {
        ...state.crmPagination,
        loading: true,
      },
    });

    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await api.get(
        `api/enterprises/${enterpriseId}/contacts/?limit=${pageSize}&offset=${offset}${searchParam}`
      );

      if (response.status >= 200 && response.status < 300) {
        const results = response.data.results || [];
        const ordered = sortByStatusAndName(results, "name", "telephone");
        setState({
          crm_contacts: ordered,
          crmPagination: {
            page,
            pageSize,
            total: response.data.count || 0,
            loading: false,
          },
        });
      } else {
        setState({
          crmPagination: {
            ...state.crmPagination,
            loading: false,
          },
        });
      }
    } catch (error) {
      setState({
        crmPagination: {
          ...state.crmPagination,
          loading: false,
        },
      });
    }
  };

  const fetchExtensionContacts = async (
    page = 0,
    pageSize = 25,
    search = ""
  ) => {
    return fetchPbxContacts("EXTENSION", page, pageSize, search);
  };

  const fetchAgentContacts = async (page = 0, pageSize = 25, search = "") => {
    return fetchPbxContacts("AGENT", page, pageSize, search);
  };

  const fetchQueueContacts = async (page = 0, pageSize = 25, search = "") => {
    return fetchPbxContacts("QUEUE", page, pageSize, search);
  };

  const fetchPbxContacts = async (
    contact_type,
    page,
    pageSize,
    search = ""
  ) => {
    const enterpriseId = getSession().profile.enterprise.id;
    const agentId = getSession().profile.agent_id;
    const offset = page * pageSize;
    const label = contact_type.toLowerCase();

    setState({
      [`${label}Pagination`]: {
        ...state[`${label}Pagination`],
        loading: true,
      },
    });

    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await api.get(
        `/api/enterprises/${enterpriseId}/agents/${agentId}/iv_contacts/?limit=${pageSize}&offset=${offset}&contact_type=${contact_type}${searchParam}`,
        { timeout: 15000 }
      );
      if (response.status >= 200 && response.status < 300) {
        const rawContacts = response.data[`${label}_contacts`] || [];
        let orderedContacts;
        if (label === "agent") {
          // Operadores: usa "name" como principal
          orderedContacts = sortByStatusAndName(rawContacts, "name", "user");
        } else if (label === "extension") {
          // Ramais: usa "description" e cai para "username"
          orderedContacts = sortByStatusAndName(
            rawContacts,
            "description",
            "username"
          );
        } else if (label === "queue") {
          // Filas: usa "description" e cai para "name"
          orderedContacts = sortByStatusAndName(
            rawContacts,
            "description",
            "name"
          );
        } else {
          // fallback genérico
          orderedContacts = sortByStatusAndName(rawContacts, "name");
        }

        setState({
          [`${label}_contacts`]: orderedContacts,
          [`${label}Pagination`]: {
            page,
            pageSize,
            total:
              response.data[`${label}_contacts_count`] ||
              rawContacts.length,
            loading: false,
          },
        });
      } else {
        setState({
          [`${label}Pagination`]: {
            ...state[`${label}Pagination`],
            loading: false,
          },
        });
      }
    } catch (error) {
      setState({
        [`${label}Pagination`]: {
          ...state[`${label}Pagination`],
          loading: false,
        },
      });
    }
  };

  const handleSearchChange = useCallback(
    (value) => {
      setSearchTerm(value);

      // Clear existing timeout
      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId);
      }

      // Set new timeout
      const newTimeoutId = setTimeout(() => {
        // Reset to first page and fetch with search term
        fetchCrmContacts(0, state.crmPagination.pageSize, value);
        fetchExtensionContacts(0, state.extensionPagination.pageSize, value);
        fetchAgentContacts(0, state.agentPagination.pageSize, value);
        fetchQueueContacts(0, state.queuePagination.pageSize, value);
      }, 500);

      setSearchTimeoutId(newTimeoutId);
    },
    [
      searchTimeoutId,
      state.crmPagination.pageSize,
      state.extensionPagination.pageSize,
      state.agentPagination.pageSize,
      state.queuePagination.pageSize,
    ]
  );

  // Update accordion expansion based on search results
  useEffect(() => {
    if (searchTerm.trim()) {
      // When searching, expand accordions with results and collapse empty ones
      setExpandedAccordions({
        queues: state.queuePagination.total > 0,
        agents: state.agentPagination.total > 0,
        extensions: state.extensionPagination.total > 0,
        contacts: state.crmPagination.total > 0,
      });
    } else {
      // When not searching, collapse all accordions
      setExpandedAccordions({
        queues: false,
        agents: false,
        extensions: false,
        contacts: false,
      });
    }
  }, [
    searchTerm,
    state.queuePagination.total,
    state.agentPagination.total,
    state.extensionPagination.total,
    state.crmPagination.total,
  ]);

  const handleAccordionToggle = (accordionName) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionName]: isExpanded,
    }));
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId);
      }
    };
  }, [searchTimeoutId]);

  useEffect(async () => {
    const enterpriseId = getSession().profile.enterprise.id;
    const agentId = getSession().profile.agent_id;

    // Fetch CRM contacts with pagination
    await fetchCrmContacts(0, 25);

    // Fetch extension contacts with pagination
    await fetchExtensionContacts(0, 25);

    await fetchAgentContacts(0, 25);

    await fetchQueueContacts(0, 25);

    return () => {};
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Agenda</DialogTitle>
      <DialogContent>
        <TextField
          label="Busca"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Digite o número ou nome do contato"
        />
        <PbxContacts
          label="Contatos"
          contacts={state.crm_contacts}
          total={state.crmPagination.total}
          columns={[
            { field: "name", headerName: "Nome", width: 200, flex: 1 },
            { field: "telephone", headerName: "Telefone", width: 150 },
            {
              field: "contact_enterprise_name",
              headerName: "Empresa",
              width: 150,
            },
            {
              field: "status",
              headerName: "Status",
              width: 120,
              renderCell: (params) => <StatusCell value={params.value} />,
            },
          ]}
          page={state.crmPagination.page}
          pageSize={state.crmPagination.pageSize}
          fetchContacts={fetchCrmContacts}
          loading={state.crmPagination.loading}
          searchTerm={searchTerm}
          expanded={expandedAccordions.contacts}
          onToggle={handleAccordionToggle("contacts")}
          onContactSelected={(event, contact) => {
            handleContactSelected(event, { number: contact.telephone.replace(/\D/g, "") });
            onClose();
          }}
        />

        <PbxContacts
          label="Operadores"
          contacts={state.agent_contacts}
          total={state.agentPagination.total}
          columns={[
            { field: "name", headerName: "Nome", width: 200, flex: 1 },
            { field: "user", headerName: "Usuário", width: 150 },
            {
              field: "status",
              headerName: "Status",
              width: 120,
              renderCell: (params) => <StatusCell value={params.value} />,
            },
          ]}
          page={state.agentPagination.page}
          pageSize={state.agentPagination.pageSize}
          fetchContacts={fetchAgentContacts}
          loading={state.agentPagination.loading}
          searchTerm={searchTerm}
          expanded={expandedAccordions.agents}
          onToggle={handleAccordionToggle("agents")}
          onContactSelected={(event, contact) => {
            handleContactSelected(event, { number: contact.user });
            onClose();
          }}
        />
        <PbxContacts
          label="Ramais"
          contacts={state.extension_contacts}
          total={state.extensionPagination.total}
          columns={[
            { field: "description", headerName: "Nome", width: 200, flex: 1 },
            { field: "username", headerName: "Ramal", width: 150 },
            {
              field: "status",
              headerName: "Status",
              width: 120,
              renderCell: (params) => <StatusCell value={params.value} />,
            },
          ]}
          page={state.extensionPagination.page}
          pageSize={state.extensionPagination.pageSize}
          fetchContacts={fetchExtensionContacts}
          loading={state.extensionPagination.loading}
          searchTerm={searchTerm}
          expanded={expandedAccordions.extensions}
          onToggle={handleAccordionToggle("extensions")}
          onContactSelected={(event, contact) => {
            const number =  contact.username.includes('_') ? contact.username.split('_')[1] : contact.username;
            handleContactSelected(event, { number });
            onClose();
          }}
        />

        <PbxContacts
          label="Filas"
          contacts={state.queue_contacts}
          total={state.queuePagination.total}
          columns={[
            { field: "description", headerName: "Nome", width: 200, flex: 1 },
            { field: "name", headerName: "Código", width: 150 },
            {
              field: "status",
              headerName: "Status",
              width: 120,
              renderCell: (params) => <StatusCell value={params.value} />,
            },
          ]}
          page={state.queuePagination.page}
          pageSize={state.queuePagination.pageSize}
          fetchContacts={fetchQueueContacts}
          loading={state.queuePagination.loading}
          searchTerm={searchTerm}
          expanded={expandedAccordions.queues}
          onToggle={handleAccordionToggle("queues")}
          onContactSelected={(event, contact) => {
            const number =  contact.name.includes('_') ? contact.name.split('_')[1] : contact.name;
            handleContactSelected(event, { number });
            onClose();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactsPanel;
